export interface MetricsState {
  pixiFps: number
  updateCount: number
  updatesPerSecond: number
  droppedFrames: number
  lastFrameTime: number
  frameCallbackCount: number
  expectedFrames: number
}

export class MetricsTracker {
  private metrics: MetricsState = {
    pixiFps: 0,
    updateCount: 0,
    updatesPerSecond: 0,
    droppedFrames: 0,
    lastFrameTime: 0,
    frameCallbackCount: 0,
    expectedFrames: 0
  }

  private updateCounts: number[] = []
  private lastSecond = 0
  private countThisSecond = 0
  private fpsHistory: number[] = []
  private lastFpsUpdate = 0

  reset(): void {
    this.metrics = {
      pixiFps: 0,
      updateCount: 0,
      updatesPerSecond: 0,
      droppedFrames: 0,
      lastFrameTime: 0,
      frameCallbackCount: 0,
      expectedFrames: 0
    }
    this.updateCounts = []
    this.lastSecond = 0
    this.countThisSecond = 0
    this.fpsHistory = []
    this.lastFpsUpdate = 0
  }

  recordTextureUpdate(): void {
    this.metrics.updateCount++
    const now = performance.now()
    const currentSecond = Math.floor(now / 1000)

    if (currentSecond !== this.lastSecond) {
      this.updateCounts.push(this.countThisSecond)
      if (this.updateCounts.length > 5) {
        this.updateCounts.shift()
      }
      this.metrics.updatesPerSecond = this.countThisSecond
      this.countThisSecond = 0
      this.lastSecond = currentSecond
    }
    this.countThisSecond++
  }

  recordFrameCallback(mediaTime: number, _expectedDisplayTime: number): void {
    this.metrics.frameCallbackCount++
    this.metrics.lastFrameTime = mediaTime
  }

  updatePixiFps(deltaMs: number): void {
    const now = performance.now()
    if (now - this.lastFpsUpdate > 100) {
      const fps = 1000 / deltaMs
      this.fpsHistory.push(fps)
      if (this.fpsHistory.length > 10) {
        this.fpsHistory.shift()
      }
      this.metrics.pixiFps = Math.round(
        this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length
      )
      this.lastFpsUpdate = now
    }
  }

  calculateDroppedFrames(videoFps: number, elapsedTime: number, actualFrames: number): void {
    const expected = Math.floor(elapsedTime * videoFps)
    this.metrics.expectedFrames = expected
    this.metrics.droppedFrames = Math.max(0, expected - actualFrames)
  }

  getMetrics(): MetricsState {
    return { ...this.metrics }
  }

  getUpdatesPerSecond(): number {
    return this.metrics.updatesPerSecond
  }
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function formatTimeDetailed(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = (seconds % 60).toFixed(2)
  return `${mins}:${secs.padStart(5, '0')}`
}
